<script setup lang="ts">
import { ref } from 'vue';
import { default as ButtonEl } from '@/components/shared/Button.vue';
import BaseOverlay from '@/overlays/BaseOverlay.vue';
import AddSuggestionOverlay from '@/views/meetings/AddSuggestionOverlay.vue';
import { IMeeting, ISuggestion } from '@/stores/meetings';

const emit = defineEmits(['close']);

const showAddSuggestionOverlay = ref<boolean>(false);

interface Props {
  suggestions: ISuggestion[];
  meeting: IMeeting;
}

const props = defineProps<Props>();
</script>

<template>
  <BaseOverlay @close="emit('close')" :title="$t('meeting.suggestions')">
    <template #content>
      <div
        v-for="(suggestion, index) in suggestions"
        :key="index"
        class="participants content inline-flex w-full mb-4 items-center"
      >
        <img
          :class="{ '-m-1': index !== 0 }"
          class="rounded-full border-2 border-neutral-0 mr-1 shadow-xsm"
          :style="{ width: '84px', height: '84px' }"
          v-optimized="{ src: suggestion.suggester.displayImage }"
        />

        <div
          class="w-full border border-neutral-300 relative px-4 py-6 -mt-1 ml-1 bg-neutral-0 shadow-smallBottom rounded-[16px]"
        >
          <p class="h600">
            {{ suggestion.suggester.displayName }}
          </p>
          <p class="b500">{{ suggestion.title }}</p>
        </div>
      </div>

      <div
        v-if="!suggestions.length"
        class="w-full flex flex-col items-center pt-24"
      >
        <fa-icon
          :icon="['fas', 'fa-lightbulb']"
          class="text-6xl text-neutral-300 mb-4"
        />
        <p class="b600">{{ $t('meeting.noSuggestionsYet') }}</p>
      </div>
    </template>

    <template #actions>
      <ButtonEl
        class="bold"
        text="meeting.writeSuggestion"
        :icon="['fal', 'fa-edit']"
        background="bg-darkOcean-600"
        textColor="text-neutral-0"
        rounded="full"
        padding="px-16"
        @click="showAddSuggestionOverlay = true"
      />
    </template>
  </BaseOverlay>

  <AddSuggestionOverlay
    v-if="showAddSuggestionOverlay"
    :meeting="props.meeting"
    @close="showAddSuggestionOverlay = false"
  />
</template>

<style lang="scss">
.suggestions {
  min-height: 1200px;
}
.meetingActions {
  bottom: 150px;
}

.write-suggestion {
  bottom: 150px;
}
</style>
